import {
  buildLocalizedShopText,
  STANDARD_CUSTOMIZATION_GROUPS,
  type ProductOptionGroup,
} from '../mocks/shopProductOptions';
import type { ShopProduct } from '../mocks/shopProducts';
import {
  collect3dlookLookupRecords,
  parse3dlookJsonRecord,
} from './mtmWidget/parse3dlookAccountData';

export type CartMeasurements = Record<string, number>;
export type CartMaterial = Record<string, number>;
export type FabricBlendEnum = string;

const STANDARD_COLOR_GROUP = STANDARD_CUSTOMIZATION_GROUPS.find((group) => group.key === 'color');
const STANDARD_FIT_GROUP = STANDARD_CUSTOMIZATION_GROUPS.find((group) => group.key === 'fit');

const DEFAULT_MEASUREMENT_KEYS = [
  'chest',
  'waist',
  'hip',
  'shoulder',
  'sleeve',
  'inseam',
  'height',
] as const;

const FABRIC_MATERIAL_MAP: Record<string, CartMaterial> = {
  Stretchy: { cotton: 95, spandex: 5 },
  Durable: { cotton: 60, polyester: 40 },
  Soft: { rayon: 70, polyester: 30 },
  Cotton: { cotton: 100 },
  Athletic: { polyester: 100 },
};

const FABRIC_LABEL_TO_ENUM: Record<string, FabricBlendEnum> = {
  stretchy: 'stretchy',
  durable: 'durable',
  soft: 'soft',
  cotton: 'cotton',
  athletic: 'athletic',
};

const FABRIC_ENUM_TO_LABEL: Record<FabricBlendEnum, string> = {
  stretchy: 'Stretchy',
  durable: 'Durable',
  soft: 'Soft',
  cotton: 'Cotton',
  athletic: 'Athletic',
};

const FIT_LABEL_TO_ENUM: Record<string, string> = {
  slim: 'slim',
  standard: 'regular',
  regular: 'regular',
  baggy: 'baggy',
};

const FIT_ENUM_TO_LABEL: Record<string, string> = {
  slim: 'Slim',
  regular: 'Standard',
  baggy: 'Baggy',
};

const GENDER_LABEL_TO_ENUM: Record<string, string> = {
  women: 'female',
  woman: 'female',
  female: 'female',
  men: 'male',
  man: 'male',
  male: 'male',
  unisex: 'unisex',
  accessories: 'unisex',
};

const GENDER_ENUM_TO_LABEL: Record<string, string> = {
  female: 'Women',
  male: 'Men',
  unisex: 'Unisex',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sanitizeKey(value: string, maxLength = 40) {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .slice(0, maxLength);
}

function sanitizeText(value: string, maxLength = 80) {
  return value.trim().replace(/\s+/g, ' ').slice(0, maxLength);
}

function normalizeFabricBlendKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function humanizeFabricBlendValue(value: string) {
  return sanitizeText(value, 80)
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export const MEASUREMENT_FIELD_CONFIG = DEFAULT_MEASUREMENT_KEYS.map((key) => ({
  key,
  label: key.charAt(0).toUpperCase() + key.slice(1),
}));

export function normalizeSizeValue(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = sanitizeText(value, 24);
  return trimmed || undefined;
}

export function normalizeFitLabel(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  const enumValue = FIT_LABEL_TO_ENUM[normalized];
  return enumValue ? FIT_ENUM_TO_LABEL[enumValue] : undefined;
}

export function fitLabelToEnum(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  return FIT_LABEL_TO_ENUM[value.trim().toLowerCase()];
}

export function normalizeGenderLabel(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  const enumValue = GENDER_LABEL_TO_ENUM[normalized];
  return enumValue ? GENDER_ENUM_TO_LABEL[enumValue] : undefined;
}

export function genderLabelToEnum(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  return GENDER_LABEL_TO_ENUM[value.trim().toLowerCase()];
}

export function normalizeColorHex(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim().toUpperCase();
  const candidate = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9A-F]{6}$/.test(candidate) ? candidate : undefined;
}

function normalizeComparableColorValue(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const normalizedHex = normalizeColorHex(value);
  if (normalizedHex) {
    return normalizedHex.toLowerCase();
  }

  return value.trim().toLowerCase();
}

export function isDefaultProductColorValue(product: ShopProduct | undefined, value: unknown) {
  const normalizedValue = normalizeComparableColorValue(value);
  if (!product || !normalizedValue) {
    return false;
  }

  const colorGroup = getProductCartOptionGroups(product).find(
    (group): group is Extract<ProductOptionGroup, { type: 'color' }> => group.key === 'color' && group.type === 'color'
  );
  const defaultColor = colorGroup?.defaultValue;
  if (!defaultColor) {
    return false;
  }

  const defaultLabels = typeof defaultColor.label === 'string'
    ? [defaultColor.label]
    : defaultColor.label && typeof defaultColor.label === 'object'
      ? Object.values(defaultColor.label)
      : [];
  const candidates = [defaultColor.hex, ...defaultLabels];

  return candidates
    .map((candidate) => normalizeComparableColorValue(candidate))
    .filter(Boolean)
    .includes(normalizedValue);
}

export function normalizeMeasurementsRecord(value: unknown): CartMeasurements | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value).reduce<[string, number][]>((accumulator, [key, rawValue]) => {
    const safeKey = sanitizeKey(key);
    const numericValue =
      typeof rawValue === 'number'
        ? rawValue
        : typeof rawValue === 'string'
          ? Number.parseFloat(rawValue)
          : Number.NaN;

    if (!safeKey || !Number.isFinite(numericValue) || numericValue <= 0) {
      return accumulator;
    }

    accumulator.push([safeKey, Number(numericValue.toFixed(2))]);
    return accumulator;
  }, []);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function normalizeMaterialRecord(value: unknown): CartMaterial | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value).reduce<[string, number][]>((accumulator, [key, rawValue]) => {
    const safeKey = sanitizeKey(key);
    const numericValue =
      typeof rawValue === 'number'
        ? rawValue
        : typeof rawValue === 'string'
          ? Number.parseFloat(rawValue)
          : Number.NaN;

    if (!safeKey || !Number.isFinite(numericValue) || numericValue < 0) {
      return accumulator;
    }

    accumulator.push([safeKey.toLowerCase(), Number(numericValue.toFixed(2))]);
    return accumulator;
  }, []);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function buildMaterialFromFabricLabel(fabricLabel: string | undefined) {
  if (!fabricLabel) {
    return undefined;
  }

  const material = FABRIC_MATERIAL_MAP[sanitizeText(fabricLabel, 32)];
  return material ? { ...material } : undefined;
}

export function normalizeFabricBlendLabel(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = normalizeFabricBlendKey(value);
  const enumValue = FABRIC_LABEL_TO_ENUM[normalized];
  return enumValue ? FABRIC_ENUM_TO_LABEL[enumValue] : humanizeFabricBlendValue(value);
}

export function fabricBlendLabelToEnum(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = normalizeFabricBlendKey(value);
  if (!normalizedValue) {
    return undefined;
  }

  return FABRIC_LABEL_TO_ENUM[normalizedValue] ?? normalizedValue;
}

export function getFabricLabelFromMaterial(material: CartMaterial | undefined) {
  if (!material) {
    return undefined;
  }

  const signature = JSON.stringify(Object.entries(material).sort(([left], [right]) => left.localeCompare(right)));
  const match = Object.entries(FABRIC_MATERIAL_MAP).find(([, value]) => {
    const candidateSignature = JSON.stringify(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)));
    return candidateSignature === signature;
  });

  return match?.[0];
}

export function fabricBlendEnumToLabel(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = normalizeFabricBlendKey(value);
  return FABRIC_ENUM_TO_LABEL[normalized] ?? humanizeFabricBlendValue(value);
}

export function getDefaultGenderLabel(product: ShopProduct) {
  switch (product.audience) {
    case 'Men':
      return 'Men';
    case 'Women':
      return 'Women';
    default:
      return 'Women';
  }
}

export interface ProductCartOptionGroupOptions {
  defaultSizingProfile?: 'Men' | 'Women';
  hideFitOption?: boolean;
  hideCustomSizeOption?: boolean;
}

function applyContextualOptionDefaults(
  optionGroups: ProductOptionGroup[],
  options: ProductCartOptionGroupOptions = {},
) {
  return optionGroups.map((group) => {
    if (group.type === 'button' && group.key === 'size' && options.hideCustomSizeOption) {
      return {
        ...group,
        options: group.options.filter((option) => option.value !== 'Custom'),
        defaultValue: undefined,
      };
    }

    if (group.type === 'button' && group.key === 'size') {
      return { ...group, defaultValue: undefined };
    }

    if (group.type === 'button' && group.key === 'fit') {
      return { ...group, defaultValue: undefined };
    }

    if (group.type === 'button' && group.key === 'gender') {
      return { ...group, defaultValue: options.defaultSizingProfile };
    }

    return group;
  });
}

export function getProductCartOptionGroups(
  product: ShopProduct,
  options: ProductCartOptionGroupOptions = {},
): ProductOptionGroup[] {
  const baseOptionGroups = product.optionGroups ?? [
    {
      key: 'size',
      label: buildLocalizedShopText('Size', '尺码'),
      type: 'button' as const,
      options: product.sizes.map((value) => ({ value, label: value })),
      pill: true,
      required: true,
    },
  ];
  const usesSingleSizeCategory =
    product.category === 'Hair'
    || product.category === 'Nails'
    || product.category === 'Makeup';

  const nextOptionGroups = baseOptionGroups.filter((group) => (
    group.key !== 'gender'
  ));

  if (usesSingleSizeCategory) {
    return applyContextualOptionDefaults(nextOptionGroups, options);
  }

  // Fit is always shown in the product page UI and must be explicitly selected.
  if (!options.hideFitOption && STANDARD_FIT_GROUP && !nextOptionGroups.some((group) => group.key === 'fit')) {
    nextOptionGroups.push({ ...STANDARD_FIT_GROUP, required: true });
  }

  if (STANDARD_COLOR_GROUP && !nextOptionGroups.some((group) => group.key === 'color')) {
    nextOptionGroups.push(STANDARD_COLOR_GROUP);
  }

  return applyContextualOptionDefaults(nextOptionGroups, options);
}

export function buildCartDetails({
  categoryLabel,
  size,
  fit,
  fabricLabel,
  color,
  gender,
}: {
  categoryLabel?: string;
  size?: string;
  fit?: string;
  fabricLabel?: string;
  color?: string;
  gender?: string;
}) {
  const details = {
    ...(size ? { Size: [size] } : {}),
    ...(fit ? { Fit: [fit] } : {}),
    ...(fabricLabel ? { Fabric: [fabricLabel] } : {}),
    ...(color ? { Color: [color] } : {}),
    ...(gender ? { Gender: [gender] } : {}),
    ...(categoryLabel ? { Category: [categoryLabel] } : {}),
  };

  return Object.keys(details).length > 0 ? details : undefined;
}

export function buildCartDescription({
  categoryLabel,
  size,
  fit,
  fabricLabel,
  gender,
}: {
  categoryLabel?: string;
  size?: string;
  fit?: string;
  fabricLabel?: string;
  gender?: string;
}) {
  const segments = [
    categoryLabel,
    size ? `Size ${size}` : '',
    fit ? `${fit} fit` : '',
    fabricLabel ? fabricLabel : '',
    gender ? `${gender} sizing` : '',
  ].filter(Boolean);

  const description = segments.join(' · ');
  return description ? `${description}.` : '';
}

export function extractMeasurementsFromUser(user: unknown) {
  if (!isRecord(user)) {
    return undefined;
  }

  const nestedCandidates = [
    user.measurements,
    user.measurement_profile,
    user.measurementProfile,
    user.fit_profile,
    user.fitProfile,
    ...collect3dlookLookupRecords(user),
  ];

  for (const rawCandidate of nestedCandidates) {
    const candidate = isRecord(rawCandidate) ? rawCandidate : parse3dlookJsonRecord(rawCandidate);
    if (!candidate) {
      continue;
    }

    const directMeasurements = normalizeMeasurementsRecord(candidate.measurements ?? candidate);
    if (directMeasurements) {
      return directMeasurements;
    }

    const nestedMeasurementGroups = [
      candidate.measurements,
      isRecord(candidate.measurements) ? candidate.measurements.front_params : undefined,
      isRecord(candidate.measurements) ? candidate.measurements.side_params : undefined,
      isRecord(candidate.measurements) ? candidate.measurements.volume_params : undefined,
      candidate.front_params,
      candidate.side_params,
      candidate.volume_params,
      isRecord(candidate.body) ? candidate.body.measurements : candidate.body,
      isRecord(candidate.data) ? candidate.data.measurements : candidate.data,
      isRecord(candidate.state) ? candidate.state.measurements : candidate.state,
    ];

    const mergedMeasurements = normalizeMeasurementsRecord(
      nestedMeasurementGroups.reduce<Record<string, unknown>>((accumulator, group) => {
        if (!isRecord(group)) {
          return accumulator;
        }

        return {
          ...accumulator,
          ...group,
        };
      }, {
        height: candidate.height ?? (isRecord(candidate.state) ? candidate.state.height : undefined),
        weight: candidate.weight ?? (isRecord(candidate.state) ? candidate.state.weight : undefined),
      })
    );

    if (mergedMeasurements) {
      return mergedMeasurements;
    }
  }

  return undefined;
}
